import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { TakerMarket, OfferSchedule } from '@/enterprise/data/useTakerMarkets';
import { getNormalizedRate, isValidRateInput } from '@/utils/validateRate';
import { Cadence, DaysOfTheWeek, OfferModalForm, ScheduleInput, WeeklyCadenceValue } from './types';

// This is hard coded in scheduler at launch, but _could_ be dynamic in the future.
export const scheduleTimeZone = 'America/Chicago';

export const validateRateInputForTakerMarket = ({
  takerMarket,
  rateInput,
  rateType,
  t,
}: {
  takerMarket: TakerMarket;
  rateInput: string;
  rateType: 'apr' | 'disc' | undefined;
  t: TFunction;
}): {
  valid: boolean;
  errorMessage?: string;
} => {
  // Benchmark markets do not accept a rate
  if (takerMarket.marketPricingType === 'BENCHMARK') {
    return {
      valid: true,
    };
  }

  const rate = getNormalizedRate(rateInput);

  // Not a valid rate
  if (rateInput === '') {
    return {
      valid: false,
      errorMessage: t('offerSubmitDialog.errors.enterAValidRate'),
    };
  }

  // Static markets only accept rates at or above the static market rate, and we only support APR at this time.
  if (takerMarket.marketPricingType === 'STATIC') {
    const isRateValid =
      isValidRateInput(rateInput) && rateType === 'apr' && rate >= (takerMarket.staticMarketRate ?? 0);

    return {
      valid: isRateValid,
      errorMessage: isRateValid ? undefined : t('offerSubmitDialog.fixedRate.rateInputError'),
    };
  }

  // Last is checks for price discovery markets

  if (rateType === 'disc') {
    const minThreshold = 0;
    const maxRate = takerMarket.marketMaxDiscount ?? 30;

    const isRateValid = isValidRateInput(rateInput) && rate > minThreshold && rate <= maxRate;

    // Rate must be greater than zero
    if (rate <= minThreshold) {
      return {
        valid: false,
        errorMessage: t('offerSubmitDialog.errors.enterRateGreaterThanZero'),
      };
    }

    // Rate must be less than or equal to the maximum allowed rate (default 30)
    if (rate > maxRate) {
      return {
        valid: false,
        errorMessage: t('offerSubmitDialog.errors.discRateCannotBeGreaterThanMax', {
          maxRate,
        }),
      };
    }

    // Fall back if entering an invalid rate like '...'
    if (!isRateValid) {
      return {
        valid: false,
        errorMessage: t('offerSubmitDialog.errors.enterAValidRate'),
      };
    }

    return {
      valid: isRateValid,
      errorMessage: undefined,
    };
  }

  if (rateType === 'apr') {
    const minThreshold = 0;
    const maxRate = takerMarket.marketMaxApr ?? 99;

    const isRateValid = isValidRateInput(rateInput) && rate >= minThreshold && rate <= maxRate;

    // Rate must be greater than zero
    if (rate <= minThreshold) {
      return {
        valid: false,
        errorMessage: t('offerSubmitDialog.errors.enterRateGreaterThanZero'),
      };
    }

    // Rate must be less than or equal to the maximum allowed rate (default 99)
    if (rate > maxRate) {
      return {
        valid: false,
        errorMessage: t('offerSubmitDialog.errors.aprRateCannotBeGreaterThanMax', {
          maxRate,
        }),
      };
    }

    // Fall back if entering an invalid rate like '...'
    if (!isRateValid) {
      return {
        valid: false,
        errorMessage: t('offerSubmitDialog.errors.enterAValidRate'),
      };
    }

    return {
      valid: isRateValid,
      errorMessage: undefined,
    };
  }

  return {
    valid: false,
    errorMessage: t('offerSubmitDialog.errors.enterAValidRate'),
  };
};

export const validateCadence = (cadence: Cadence, t: TFunction) => {
  if (cadence.type === 'weekly' && cadence.value?.length === 0) {
    return {
      valid: false,
      errorMessage: t('scheduling.selectDayOrReset'),
    };
  }

  return {
    valid: true,
    errorMessage: undefined,
  };
};

export const getDefaultRateType = (takerMarket: TakerMarket) => {
  if (takerMarket.marketPricingType === 'STATIC') {
    return 'apr';
  }

  return !takerMarket.offerConfig.uuid || takerMarket.offerConfig.isDiscountBidding === null
    ? undefined // default to undefined for net new offers
    : takerMarket.offerConfig.isDiscountBidding
    ? 'disc'
    : 'apr';
};

/**
 * Checks if the given duration values equates to the same as a regular ongoing "always on" offer,
 * aka there should not be an offer schedule entity at all.
 */
export const checkIfDurationIsATypicalOngoingOffer = ({
  startDate,
  endDate,
  cadence,
}: {
  startDate: OfferModalForm['startDate'];
  endDate: OfferModalForm['endDate'];
  cadence: OfferModalForm['cadence'];
}) => {
  return startDate === undefined && endDate === undefined && cadence.type === 'daily';
};

export const buildCustomScheduleSentenceFromExistingOfferSchedule = ({
  offerSchedule,
  t,
}: {
  offerSchedule: OfferSchedule;
  t: TFunction;
}) => {
  const { startDate, endDate, schedule } = offerSchedule;
  const cadence = buildCadenceFromExistingOfferSchedule(schedule);

  return buildCustomScheduleSentenceFromFormData({ startDate, endDate, cadence, t });
};

export const buildCustomScheduleSentenceFromFormData = ({
  startDate,
  endDate,
  cadence,
  t,
}: {
  startDate: OfferModalForm['startDate']; // YYYY-MM-DD
  endDate: OfferModalForm['endDate']; // YYYY-MM-DD
  cadence: OfferModalForm['cadence'] | null;
  t: TFunction;
}) => {
  if (cadence && checkIfDurationIsATypicalOngoingOffer({ startDate, endDate, cadence })) {
    return t('scheduling.ongoing');
  }

  const startDateFormatted = startDate
    ? format(new Date(`${startDate}T00:00:00`), 'yyyy-MM-dd').replace(/-/g, '‑')
    : undefined;
  const endDateFormatted = endDate
    ? format(new Date(`${endDate}T00:00:00`), 'yyyy-MM-dd').replace(/-/g, '‑')
    : undefined;
  const daysOfWeekCommaSeparated = cadence?.value
    ?.sort((a: WeeklyCadenceValue, b: WeeklyCadenceValue) => {
      return DaysOfTheWeek.indexOf(a) - DaysOfTheWeek.indexOf(b);
    })
    .map((day: WeeklyCadenceValue) => t(`scheduling.daysOfWeek.${day}`))
    .join(', ');

  if (cadence?.type === 'daily') {
    // Participates Daily starting 2025-03-25 through 2025-04-05
    if (startDate && endDate) {
      return t('scheduling.dailyWithStartDateAndEndDate', {
        startDate: startDateFormatted,
        endDate: endDateFormatted,
      });
    }

    // Participates Daily starting 2025-03-25
    if (startDate && !endDate) {
      return t('scheduling.dailyWithStartDate', {
        startDate: startDateFormatted,
      });
    }

    // Participates Daily ending 2025-04-05
    if (!startDate && endDate) {
      return t('scheduling.dailyWithEndDate', {
        endDate: endDateFormatted,
      });
    }
  }

  if (cadence?.type === 'weekly' && cadence?.value && cadence?.value.length > 0) {
    // Participates every Monday, Wednesday, Friday starting 2025-03-25 through 2025-04-05
    if (startDate && endDate) {
      return t('scheduling.daysOfWeekWithStartDateAndEndDate', {
        daysOfWeekCommaSeparated,
        startDate: startDateFormatted,
        endDate: endDateFormatted,
      });
    }

    // Participates every Monday, Wednesday, Friday starting 2025-03-25
    if (startDate && !endDate) {
      return t('scheduling.daysOfWeekWithStartDate', {
        daysOfWeekCommaSeparated,
        startDate: startDateFormatted,
      });
    }

    // Participates every Monday, Wednesday, Friday ending 2025-04-05
    if (!startDate && endDate) {
      return t('scheduling.daysOfWeekWithEndDate', {
        daysOfWeekCommaSeparated,
        endDate: endDateFormatted,
      });
    }

    // Participates every Monday, Wednesday, Friday
    if (!startDate && !endDate) {
      return t('scheduling.daysOfWeek', {
        daysOfWeekCommaSeparated,
      });
    }
  }

  // If for some reason we don't catch any other condition, fall back to generic custom language
  return t('scheduling.custom');
};

/**
 * The default schedule cadence is daily, every single day of the week.
 * We can use this value as a form default, but also
 * to translate a recurring schedule that is on every day with only a "start" or "end" date to show as "Daily" in the UI.
 */
export const defaultCadence: Cadence = { type: 'daily', value: DaysOfTheWeek };

export const buildCadenceFromExistingOfferSchedule = (schedule?: OfferSchedule['schedule']): Cadence => {
  if (!schedule) {
    return defaultCadence;
  }

  // Recurring schedules are always weekly
  if ('daysOfWeek' in schedule) {
    const daysOfWeek = schedule.daysOfWeek ?? [];
    return DaysOfTheWeek.every((day) => daysOfWeek.includes(day))
      ? defaultCadence
      : { type: 'weekly', value: daysOfWeek };
  }

  // Future schedules are always daily
  if ('frequency' in schedule) {
    return { type: 'daily', value: DaysOfTheWeek };
  }

  return defaultCadence;
};

export const buildSchedulePatchData = (schedule: ScheduleInput, takerMarket?: TakerMarket) => {
  if (schedule === undefined) {
    return undefined;
  }

  if (schedule === null) {
    return null;
  }

  let startDate = schedule.startDate;

  if (schedule.startDate === takerMarket?.offerSchedule?.startDate) {
    /**
     * Do not patch the start date if the value did not change.
     * This is important for start date specifically, because trying to update an offer with a start date that is in the past will fail.
     */
    startDate = undefined;
  } else if (schedule.startDate === undefined) {
    /**
     * A start date will always be set by platform, even if the user doesn't initially (POST) send one, it will default to today's date in the schedule's time zone.
     * Therefore, if a user "removes" the start date in the UI and attempts to update the schedule, we need to translate that to "today" because otherwise
     * sending undefined will result in the start date remaining unchanged (due to the nature of PATCH), and null is not supported at this time.
     */
    const nowInScheduleTimeZone = new Date(new Date().toLocaleString('en-US', { timeZone: scheduleTimeZone }));
    startDate = format(nowInScheduleTimeZone, 'yyyy-MM-dd');
  }

  return {
    daysOfWeek: schedule?.daysOfWeek,
    startDate,
    endDate: schedule?.endDate ?? null,
  };
};

export const buildSchedulePostData = (schedule: ScheduleInput) => {
  if (schedule?.daysOfWeek) {
    return {
      daysOfWeek: schedule.daysOfWeek,
      startDate: schedule.startDate,
      endDate: schedule.endDate,
    };
  }

  return undefined;
};

export const buildScheduleOptimisticUpdateData = (
  schedule: ScheduleInput,
  takerMarket?: TakerMarket
): TakerMarket['offerSchedule'] => {
  if (!schedule || !takerMarket) {
    return undefined;
  }

  return {
    endDate: schedule.endDate,
    startDate: schedule.startDate,
    schedule: {
      daysOfWeek: schedule.daysOfWeek,
    },
    status: 'active',
    type: 'recurring',
    marketUuid: takerMarket.marketUuid,
    supplierDivisionUuid: takerMarket.takerDivisionUuid,
    makerOrganizationUuid: takerMarket.makerOrganizationUuid,
    timezone: scheduleTimeZone,
  };
};

/**
 * Helper function to check if a taker market is eligible for an offer
 */
export const isTakerMarketEligibleForOffer = (takerMarket: TakerMarket): boolean => {
  // Check if market is enabled
  if (!takerMarket.marketIsEnabled) {
    return false;
  }

  // Check there is not an active offer
  if (takerMarket.offerConfig?.isEnabled) {
    return false;
  }

  // Check if there's no schedule
  if (takerMarket.offerSchedule) {
    return false;
  }

  return true;
};
